import React, {Component, useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import ASCIIText from './bits/ASCIIText/ASCIIText';
import SteamCard from "./SteamCard";

type Locale = "ru" | "en";

type LocalizedText = Record<Locale, string>;

function detectLocale(): Locale {
    if (typeof navigator === "undefined") return "en";
    const lang = navigator.language || (navigator.languages && navigator.languages[0]) || "en";
    return lang.toLowerCase().startsWith("ru") ? "ru" : "en";
}

const locale: Locale = detectLocale();

const t = (text: LocalizedText): string => text[locale];

type LinkIcon = "steam" | "youtube" | "website" | "vk";

interface ProjectLink {
    label: string | LocalizedText;
    url: string;
    icon: LinkIcon;
}

type ProjectGroup = "games" | "apps" | "mods";

interface Project {
    title: string;
    description: LocalizedText;
    release?: LocalizedText;
    media: { type: "steam"; appId: string; header?: string; trailer?: string } | { type: "img"; src: string };
    links: ProjectLink[];
    group: ProjectGroup;
}

const GROUPS: { key: ProjectGroup; title: LocalizedText }[] = [
    {key: "games", title: {ru: "Игры", en: "Games"}},
    {key: "apps", title: {ru: "Игровые приложения", en: "Game Apps"}},
    {key: "mods", title: {ru: "Моды", en: "Mods"}},
];

const websiteLabel: LocalizedText = {ru: "Сайт", en: "Website"};
const comingSoon: LocalizedText = {ru: "Скоро в раннем доступе", en: "Coming soon to Early Access"};

const contactLinks: { label: LocalizedText; href: string; internal?: boolean }[] = [
    {label: {ru: "Резюме", en: "Resume"}, href: "/resume", internal: true},
    {label: {ru: "Telegram: @swathingsoap", en: "Telegram: @swathingsoap"}, href: "https://t.me/swathingsoap"},
];

const EXPERIENCE_START_YEAR = 2019;

// Russian plural forms: [1, 2-4, 5+]
function pluralRu(n: number, forms: [string, string, string]): string {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return forms[0];
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
    return forms[2];
}

function CountUp({to, suffix = ""}: { to: number; suffix?: string }) {
    const ref = useRef<HTMLSpanElement>(null);
    const [value, setValue] = useState(0);

    useEffect(() => {
        const el = ref.current;
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (!el || reduceMotion || typeof IntersectionObserver === "undefined") {
            setValue(to);
            return;
        }

        let frame = 0;
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            const start = performance.now();
            const duration = 1200;
            const tick = (now: number) => {
                const progress = Math.min((now - start) / duration, 1);
                const eased = 1 - Math.pow(1 - progress, 3);
                setValue(Math.round(eased * to));
                if (progress < 1) frame = requestAnimationFrame(tick);
            };
            frame = requestAnimationFrame(tick);
        }, {threshold: 0.5});

        observer.observe(el);
        return () => {
            observer.disconnect();
            cancelAnimationFrame(frame);
        };
    }, [to]);

    return <span ref={ref}>{value}{suffix}</span>;
}

const SteamIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
        <circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" strokeWidth="1.4"/>
        <circle cx="8.4" cy="14.6" r="2.1" fill="none" stroke="currentColor" strokeWidth="1.3"/>
        <circle cx="14.6" cy="9.2" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.3"/>
        <line x1="9.9" y1="13.4" x2="12.9" y2="10.9" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
);

const YoutubeIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
        <rect x="2.5" y="6" width="19" height="12" rx="4" fill="none" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M10.2 9.5v5l4.6-2.5-4.6-2.5z"/>
    </svg>
);

const WebsiteIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4">
        <circle cx="12" cy="12" r="9.5"/>
        <ellipse cx="12" cy="12" rx="4" ry="9.5"/>
        <line x1="2.5" y1="12" x2="21.5" y2="12"/>
    </svg>
);

const VkIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4">
        <rect x="2.5" y="2.5" width="19" height="19" rx="5"/>
        <text x="12" y="16" fontSize="9" textAnchor="middle" fill="currentColor" stroke="none" fontFamily="Exo 2">VK</text>
    </svg>
);

const UnityIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <title>Unity</title>
        <path d="m12.9288 4.2939 3.7997 2.1929c.1366.077.1415.2905 0 .3675l-4.515 2.6076a.4192.4192 0 0 1-.4246 0L7.274 6.8543c-.139-.0745-.1415-.293 0-.3675l3.7972-2.193V0L1.3758 5.5977V16.793l3.7177-2.1456v-4.3858c-.0025-.1565.1813-.2682.318-.1838l4.5148 2.6076a.4252.4252 0 0 1 .2136.3676v5.2127c.0025.1565-.1813.2682-.3179.1838l-3.7996-2.1929-3.7178 2.1457L12 24l9.6954-5.5977-3.7178-2.1457-3.7996 2.1929c-.1341.082-.3229-.0248-.3179-.1838V13.053c0-.1565.087-.2956.2136-.3676l4.5149-2.6076c.134-.082.3228.0224.3179.1838v4.3858l3.7177 2.1456V5.5977L12.9288 0Z"/>
    </svg>
);

const icons: Record<LinkIcon, () => JSX.Element> = {
    steam: SteamIcon,
    youtube: YoutubeIcon,
    website: WebsiteIcon,
    vk: VkIcon,
};

const projects: Project[] = [
    {
        title: "Legatus",
        description: {
            ru: "Симулятор римского легиона: жизнь лагеря и снабжение, строительство, дипломатия с племенами, тактические сражения.",
            en: "Roman legion simulator: camp life and supplies, construction, tribal diplomacy and tactical battles.",
        },
        release: comingSoon,
        media: {
            type: "steam",
            appId: "4335630",
            trailer: "https://video.akamai.steamstatic.com/store_trailers/4335630/629220349/67f754d95f31cd89925d9ae7cc6c076e7b91f908/1770741728/hls_264_master.m3u8",
            header: "3de5c8b6c9d61ef554194f39831331720cb639dd/header.jpg",
        },
        links: [
            {label: "Steam", url: "https://store.steampowered.com/app/4335630/Legatus/", icon: "steam"},
        ],
        group: "games",
    },
    {
        title: "Field Orders: Eastern Front",
        description: {
            ru: "Оперативный варгейм в реальном времени о Восточном фронте Второй мировой: от полка до целых армий, снабжение и окружения.",
            en: "Real-time operational WWII wargame on the Eastern Front: from regiments to whole armies, supply lines and encirclements.",
        },
        release: comingSoon,
        media: {
            type: "steam",
            appId: "5081740",
            trailer: "https://video.akamai.steamstatic.com/store_trailers/5081740/1577612576/cf14b57717538c1c45c5abc03f60818f97533761/1791353040/hls_264_master.m3u8",
            header: "93ae3d1da188c22a5bbe94ba71cd3084f9567679/header.jpg",
        },
        links: [
            {label: "Steam", url: "https://store.steampowered.com/app/5081740/Field_Orders_Eastern_Front/", icon: "steam"},
        ],
        group: "games",
    },
    {
        title: "Ultimate General: American Revolution",
        description: {
            ru: "Стратегия-песочница о Войне за независимость США: глобальная кампания и масштабные тактические сражения.",
            en: "Sandbox strategy game about the American Revolutionary War: a grand campaign and large-scale tactical battles.",
        },
        release: {ru: "Декабрь 2024", en: "December 2024"},
        media: {
            type: "steam",
            appId: "1901910",
            trailer: "https://video.akamai.steamstatic.com/store_trailers/1901910/635457/a93745c66b559cc7777cef3d20d01a59ba269ea0/1750740442/hls_264_master.m3u8",
        },
        links: [
            {label: websiteLabel, url: "https://www.ug1775.com/", icon: "website"},
            {label: "Steam", url: "https://store.steampowered.com/app/1901910/Ultimate_General_American_Revolution/", icon: "steam"},
        ],
        group: "games",
    },
    {
        title: "The Underground Man 2",
        description: {
            ru: "Смесь жанров (beat 'em up, point-and-click, платформер) в постапокалиптическом сеттинге.",
            en: "A mix of genres (beat 'em up, point-and-click, platformer) in a post-apocalyptic setting.",
        },
        release: {ru: "Сентябрь 2022", en: "September 2022"},
        media: {
            type: "steam",
            appId: "1486950",
            trailer: "https://video.akamai.steamstatic.com/store_trailers/1486950/583236/85c198d3c92c9f6f8e3454171773924897c21ab7/1750994068/hls_264_master.m3u8",
        },
        links: [
            {label: "YouTube", url: "https://youtu.be/-Gayh8aTU4Y", icon: "youtube"},
            {label: "Steam", url: "https://store.steampowered.com/app/1486950/The_Underground_Man_2/", icon: "steam"},
            {label: "VK Play", url: "https://vkplay.ru/play/game/theundergroundman2", icon: "vk"},
        ],
        group: "games",
    },
    {
        title: "CALL OF LDPR",
        description: {
            ru: "Сюжетный шутер от первого лица с открытым 3D-миром. 75% положительных отзывов в Steam.",
            en: "Story-driven first-person shooter with an open 3D world. 75% positive reviews on Steam.",
        },
        release: {ru: "Январь 2020", en: "January 2020"},
        media: {
            type: "steam",
            appId: "1449000",
            trailer: "https://video.akamai.steamstatic.com/store_trailers/1449000/338760/fc691b54c6fd022eee12bdec8bb3de1ce85efd3f/1750989553/hls_264_master.m3u8",
        },
        links: [
            {label: "YouTube", url: "https://youtu.be/mbgn7hE2jEI", icon: "youtube"},
            {label: "Steam", url: "https://store.steampowered.com/app/1449000/CALL_OF_LDPR/", icon: "steam"},
        ],
        group: "games",
    },
    {
        title: "MGE",
        description: {
            ru: "Онлайн-ивент для стримеров Twitch в формате настольной игры: клиент-серверная архитектура и множество взаимосвязанных механик.",
            en: "Online Twitch streamer event in a board game format: client-server architecture and many interconnected mechanics.",
        },
        media: {type: "img", src: "https://i.imgur.com/JJhHhHP.jpeg"},
        links: [
            {label: "YouTube", url: "https://youtu.be/FlMpw0M67kk?t=559", icon: "youtube"},
        ],
        group: "apps",
    },
    {
        title: "The Deadly Auction Of Beauties",
        description: {
            ru: "Приложение для аукционов на стримах: генерирует персонажей по параметрам лотов.",
            en: "Live stream auction app that generates characters from lot parameters.",
        },
        media: {type: "img", src: "https://i.imgur.com/xoXS38T.jpeg"},
        links: [
            {label: "YouTube", url: "https://www.youtube.com/watch?v=XYS-zTtFvno", icon: "youtube"},
        ],
        group: "apps",
    },
    {
        title: "NASRAL",
        description: {
            ru: "Стримерский ивент по прохождению игр, предшественник NASSAL.",
            en: "Streamer game-completion event, the predecessor of NASSAL.",
        },
        media: {type: "img", src: "/nasral.png"},
        links: [],
        group: "apps",
    },
    {
        title: "NASSAL",
        description: {
            ru: "Платформа стримерского ивента по прохождению игр: аукцион игр, колесо эффектов, механики на донатах и ритм-мини-игра.",
            en: "Platform for a streamer game-completion event: game auction, effect wheel, donation mechanics and a rhythm mini-game.",
        },
        media: {type: "img", src: "/nassal-logo.png"},
        links: [
            {label: websiteLabel, url: "https://nassal.pro/", icon: "website"},
        ],
        group: "apps",
    },
    {
        title: "MegaLadder",
        description: {
            ru: "Соревновательный мультиплеер-мод для Megabonk: рейтинговые забеги на 45 минут с драфтом персонажей и предметов, синхронизация игроков через собственный сервер.",
            en: "Competitive multiplayer mod for Megabonk: 45-minute ranked runs with a character and item draft, players synced through a custom server.",
        },
        media: {type: "img", src: "/megaladder.png"},
        links: [
            {label: websiteLabel, url: "https://ladder.megabonk.su/", icon: "website"},
        ],
        group: "mods",
    },
];

function StatsStrip() {
    const years = new Date().getFullYear() - EXPERIENCE_START_YEAR;
    const steamGames = projects.filter((project) => project.media.type === "steam").length;

    const stats = [
        {
            value: years,
            suffix: "+",
            label: locale === "ru"
                ? pluralRu(years, ["год в геймдеве", "года в геймдеве", "лет в геймдеве"])
                : "years in gamedev",
        },
        {
            value: steamGames,
            label: locale === "ru"
                ? pluralRu(steamGames, ["игра в Steam", "игры в Steam", "игр в Steam"])
                : "games on Steam",
        },
    ];

    return (
        <div className="Stats">
            {stats.map((stat) => (
                <div className="Stats-item" key={stat.label}>
                    <span className="Stats-value"><CountUp to={stat.value} suffix={stat.suffix}/></span>
                    <span className="Stats-label">{stat.label}</span>
                </div>
            ))}
        </div>
    );
}

const canHoverPreview = typeof window !== "undefined"
    && window.matchMedia("(hover: hover) and (prefers-reduced-motion: no-preference)").matches;

// Skips studio logos at the start of most trailers
const TRAILER_START_SECONDS = 4;

// Muted Steam trailer that fades in over the card while it is hovered.
// Steam serves trailers as HLS: once the card scrolls into view, hls.js and the small playlist
// are fetched ahead of time, and video segments only start downloading on hover.
function HoverTrailer({src, active}: { src: string; active: boolean }) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<{ startLoad: (position?: number) => void; destroy: () => void } | null>(null);
    const nativeRef = useRef(false);
    const loadingRef = useRef(false);
    const [prepared, setPrepared] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let cancelled = false;
        const prepare = async () => {
            if (video.canPlayType("application/vnd.apple.mpegurl")) {
                nativeRef.current = true;
                setPrepared(true);
                return;
            }
            const {default: Hls} = await import("hls.js");
            if (cancelled || !Hls.isSupported()) return;
            const hls = new Hls({autoStartLoad: false, capLevelToPlayerSize: true, startLevel: 0, maxBufferLength: 8});
            hls.loadSource(src);
            hls.attachMedia(video);
            hlsRef.current = hls;
            setPrepared(true);
        };

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            prepare();
        }, {rootMargin: "200px"});
        observer.observe(video);

        return () => {
            cancelled = true;
            observer.disconnect();
            hlsRef.current?.destroy();
            hlsRef.current = null;
        };
    }, [src]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (!active) {
            video.pause();
            setVisible(false);
            return;
        }
        if (!prepared) return;

        if (!loadingRef.current) {
            loadingRef.current = true;
            if (nativeRef.current) {
                video.addEventListener("loadedmetadata", () => {
                    video.currentTime = TRAILER_START_SECONDS;
                }, {once: true});
                video.src = src;
            } else {
                hlsRef.current?.startLoad(TRAILER_START_SECONDS);
            }
        }
        video.play().catch(() => undefined);
    }, [active, prepared, src]);

    return (
        <video ref={videoRef} className={`HoverTrailer${visible ? " HoverTrailer--visible" : ""}`}
               muted loop playsInline preload="none" onPlaying={() => active && setVisible(true)}/>
    );
}

function ProjectCard({project, variant}: { project: Project; variant: "wide" | "grid" }) {
    const [hovered, setHovered] = useState(false);
    const trailer = project.media.type === "steam" && canHoverPreview ? project.media.trailer : undefined;

    return (
        <div className={`ProjectCard ProjectCard--${variant}`}
             onMouseEnter={trailer ? () => setHovered(true) : undefined}
             onMouseLeave={trailer ? () => setHovered(false) : undefined}>
            <div className="ProjectCard-media">
                {project.media.type === "steam" ? (
                    <>
                        <SteamCard appId={project.media.appId} header={project.media.header} title={project.title}
                                   storeLabel={t({ru: "в Steam", en: "on Steam"})}
                                   buttonLabel={t({ru: "Открыть в Steam", en: "View on Steam"})}/>
                        {trailer && <HoverTrailer src={trailer} active={hovered}/>}
                    </>
                ) : (
                    <img src={project.media.src} alt={project.title} loading="lazy"/>
                )}
            </div>
            <div className="ProjectCard-body">
                <p className="ProjectCard-title">
                    {project.title}
                    {project.release && <span className="ProjectCard-release"> · {t(project.release)}</span>}
                </p>
                <p className="ProjectCard-description">{t(project.description)}</p>
                <div className="ProjectCard-links">
                    {project.links.map((link) => {
                        const Icon = icons[link.icon];
                        const label = typeof link.label === "string" ? link.label : t(link.label);
                        return (
                            <a key={link.url} className="ProjectCard-link" target="_blank" rel="noreferrer"
                               href={link.url}>
                                <Icon/>
                                <span>{label}</span>
                            </a>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

class MainPage extends Component<any, any>{

    render() {
        return <>
            <header className="App-header">
                <div style={{position: "relative", width: "100%", height: "100px"}}>
                    <ASCIIText
                        text='SWATHINGSOAP'
                        enableWaves={false}
                        asciiFontSize={3}
                        textFontSize={200}
                        textColor={"#11111"}
                    />
                </div>
                <p>DMITRY EFREMOV</p>
                <p className="Role-line">
                    <span>LEAD / SENIOR</span>
                    <UnityIcon/>
                    <span>DEVELOPER</span>
                </p>
                <p className="Hero-tagline">
                    {t({ru: "Делаю игры на Unity с 2019 года.", en: "Making Unity games since 2019."})}
                </p>
                <StatsStrip/>
                <div className="Hero-links">
                    {contactLinks.map(({label, href, internal}) => internal ? (
                        <Link key={href} className="ProjectCard-link Hero-link--primary" to={href}>{t(label)}</Link>
                    ) : (
                        <a key={href} className="ProjectCard-link" href={href} target="_blank"
                           rel="noreferrer">{t(label)}</a>
                    ))}
                </div>

                <p className="Section-title">{t({ru: "ПРОЕКТЫ, НАД КОТОРЫМИ Я РАБОТАЛ", en: "PROJECTS I WORKED ON"})}</p>

                {GROUPS.map((group) => {
                    const items = projects.filter((project) => project.group === group.key);
                    if (items.length === 0) return null;

                    return (
                        <section key={group.key} className="ProjectsSection">
                            <p className="Section-title">{t(group.title)}</p>
                            <div className={group.key === "games" ? "GamesList" : "ProjectsGrid"}>
                                {items.map((project) => (
                                    <ProjectCard key={project.title} project={project}
                                                 variant={group.key === "games" ? "wide" : "grid"}/>
                                ))}
                            </div>
                        </section>
                    );
                })}
            </header>
        </>;
    }
}


export default MainPage
